import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './GuidePages.css';
import GuideAdSlot from './GuideAdSlot';
import { GUIDE_BAMBARA_CONTENT } from '../data/guideBambaraContent';

const GuideBambara: React.FC = () => {
  const { i18n } = useTranslation();
  const c = GUIDE_BAMBARA_CONTENT[i18n.language === 'en' ? 'en' : 'fr'];

  useEffect(() => {
    document.title = c.docTitle;
  }, [c.docTitle]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 jg-guide-page">

      <nav style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
        <Link to="/" style={{ color: 'var(--color-accent)', textDecoration: 'none' }}>JogoLinga</Link>
        {' › '}
        <Link to="/about" style={{ color: 'var(--color-accent)', textDecoration: 'none' }}>{c.breadcrumbGuides}</Link>
        {' › '}
        <span>Bambara</span>
      </nav>

      <h1 className="text-3xl font-bold mb-4">
        {c.pageTitle}
      </h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', fontSize: '1.05rem' }}>
        {c.intro}
      </p>

      <div className="prose prose-lg max-w-none">

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.whatIsTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.whatIsP1Intro}<em>bamanankan</em>{c.whatIsP1Meaning}<strong>{c.whatIsP1Vehicular}</strong>{c.whatIsP1After}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.whatIsP2Part1}<strong>{c.whatIsP2Manding}</strong>{c.whatIsP2Part2}<strong>{c.whatIsP2Ghana}</strong>{c.whatIsP2Part3}<strong>{c.whatIsP2Mali}</strong>{c.whatIsP2Part4}<strong>{c.whatIsP2Songhai}</strong>{c.whatIsP2Part5}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.whatIsP3Part1}<strong>{c.whatIsP3Segou}</strong>{c.whatIsP3Part2}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.tonesTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.tonesP1Part1}<strong>{c.tonesP1Strong}</strong>{c.tonesP1Part2}
          </p>
          <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem', color: 'var(--text)' }}>
            <li style={{ marginBottom: '0.5rem' }}>
              <strong>{c.toneHighLabel}</strong> : {c.toneHighDesc}
              {' '}{c.toneHighExampleLabel} <em>bá</em> {c.toneHighGloss1} {c.toneHighVs} <em>ba</em> {c.toneHighGloss2}
            </li>
            <li style={{ marginBottom: '0.5rem' }}>
              <strong>{c.toneLowLabel}</strong> : {c.toneLowDesc}
            </li>
          </ul>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.tonesP2Part1}<em>jeliya</em>{c.tonesP2Part2}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.grammarTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.grammarP1Part1}<strong>{c.grammarP1Strong1}</strong>{c.grammarP1Part2}<strong>{c.grammarP1Strong2}</strong>{c.grammarP1Part3}
          </p>
          <ul style={{ paddingLeft: '1.5rem', marginBottom: '1rem', color: 'var(--text)' }}>
            {c.particles.map((p) => (
              <li key={p.term} style={{ marginBottom: '0.5rem' }}><strong>{p.term}</strong> — {p.desc}</li>
            ))}
          </ul>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.grammarP2Label}<em>N bɛ dun</em> {c.grammarP2Gloss1}
            <em>N ye dun</em> {c.grammarP2Gloss2}
            <em>N tɛ dun</em> {c.grammarP2Gloss3}<em>dun</em> {c.grammarP2VerbGloss}
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.greetingsTitle}
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-accent)', color: '#fff' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.tableHeaderLabel}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.tableHeaderBa}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.tableHeaderPron}</th>
                </tr>
              </thead>
              <tbody>
                {c.greetingsRows.map((row) => (
                  <tr key={row.label} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 14px', color: 'var(--text)' }}>{row.label}</td>
                    <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--color-accent)' }}>{row.ba}</td>
                    <td style={{ padding: '8px 14px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>{row.pron}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.vocabTitle}
          </h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1.5rem' }}>
              <thead>
                <tr style={{ background: 'var(--color-accent)', color: '#fff' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.tableHeaderLabel}</th>
                  <th style={{ padding: '10px 14px', textAlign: 'left' }}>{c.tableHeaderBa}</th>
                </tr>
              </thead>
              <tbody>
                {c.vocabRows.map((row) => (
                  <tr key={row.label} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 14px', color: 'var(--text)' }}>{row.label}</td>
                    <td style={{ padding: '8px 14px', fontWeight: 600, color: 'var(--color-accent)' }}>{row.ba}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-4">
            {c.cultureTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.cultureP1Part1}<strong>{c.cultureP1Strong}</strong>{c.cultureP1Part2}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.cultureP2Part1}<strong>{c.cultureP2Strong1}</strong>{c.cultureP2Part2}<strong>{c.cultureP2Strong2}</strong>{c.cultureP2Part3}<strong>{c.cultureP2Strong3}</strong>{c.cultureP2Part4}
          </p>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.cultureP3Part1}<strong>{c.cultureP3Strong}</strong>{c.cultureP3Part2}
          </p>
        </section>

        <section className="mb-8 jg-guide-callout" style={{ background: 'var(--surface-raised)', borderRadius: '12px', padding: '1.5rem' }}>
          <h2 className="text-2xl font-semibold mb-4">
            {c.ctaTitle}
          </h2>
          <p className="mb-4 leading-relaxed" style={{ color: 'var(--text)' }}>
            {c.ctaText}
          </p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Link
              to="/"
              style={{
                background: 'var(--color-accent)', color: '#fff', padding: '0.75rem 1.5rem',
                borderRadius: '10px', textDecoration: 'none', fontWeight: 700, display: 'inline-block'
              }}
            >
              {c.ctaLearnButton}
            </Link>
            <Link
              to="/guide/wolof"
              style={{
                background: 'transparent', color: 'var(--color-accent)', padding: '0.75rem 1.5rem',
                borderRadius: '10px', textDecoration: 'none', fontWeight: 600,
                border: '1.5px solid var(--color-accent)', display: 'inline-block'
              }}
            >
              {c.ctaWolofButton}
            </Link>
            <Link
              to="/guide/swahili"
              style={{
                background: 'transparent', color: 'var(--color-accent)', padding: '0.75rem 1.5rem',
                borderRadius: '10px', textDecoration: 'none', fontWeight: 600,
                border: '1.5px solid var(--color-accent)', display: 'inline-block'
              }}
            >
              {c.ctaSwahiliButton}
            </Link>
          </div>
        </section>

      </div>

      <GuideAdSlot />

      <footer style={{ marginTop: '3rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
        <Link to="/" style={{ color: 'var(--color-accent)', marginRight: '1rem' }}>{c.footerHome}</Link>
        <Link to="/about" style={{ color: 'var(--color-accent)', marginRight: '1rem' }}>{c.footerAbout}</Link>
        <Link to="/guide/wolof" style={{ color: 'var(--color-accent)', marginRight: '1rem' }}>{c.footerWolof}</Link>
        <Link to="/guide/swahili" style={{ color: 'var(--color-accent)' }}>{c.footerSwahili}</Link>
      </footer>
    </div>
  );
};

export default GuideBambara;
